import React from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Collapse, Typography, Tooltip, Chip
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { F } from '../../../utils/qualFmt';
import { slugify } from '../../../utils/slug';
import { Link as RouterLink, useLocation } from 'react-router-dom';

/* ---------- helpers ---------- */

function formatDemographics(seg) {
  if (!seg || typeof seg !== 'object') return '—';
  const entries = Object.entries(seg)
    .map(([k, v]) => {
      if (v == null) return null;
      if (Array.isArray(v)) {
        const list = v.map(x => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
        return `${k}: ${list}`;
      }
      if (typeof v === 'object') {
        const inner = Object.entries(v)
          .map(([ik, iv]) => `${ik}: ${Array.isArray(iv) ? iv.join(', ') : String(iv)}`)
          .join('; ');
        return `${k}: { ${inner} }`;
      }
      return `${k}: ${String(v)}`;
    })
    .filter(Boolean);
  return entries.length ? entries.join(' • ') : '—';
}

function pickJobNode(story, jobName) {
  const arr = Array.isArray(story?.jobs_to_be_done) ? story.jobs_to_be_done : [];
  const target = slugify(jobName || '');
  return arr.find(j => slugify(j?.job_to_be_done_category || '') === target) || null;
}

/**
 * Per-interview rows for a given positive_category within the given job.
 * Source: story.positives_of_solution_choice[]  (root level)
 * We scope to stories that contain the current job.
 */
function buildInstancesFromCustomerStories({ customerStories, jobName, positiveCategory }) {
  if (!Array.isArray(customerStories) || !jobName || !positiveCategory) return [];

  const catSlug = slugify(positiveCategory);
  const rows = [];

  for (const s of customerStories) {
    const job = pickJobNode(s, jobName);
    if (!job) continue;

    const positives = Array.isArray(s.positives_of_solution_choice)
      ? s.positives_of_solution_choice
      : [];

    const matches = positives.filter(
      (p) => slugify(p?.positive_category || '') === catSlug
    );

    for (let i = 0; i < matches.length; i++) {
      const p = matches[i];
      rows.push({
        key: `${(s.customer_name || 'Interview')}-${(s.conversation_date || '')}-${catSlug}-${i}`,
        name: s.customer_name || 'Interview',
        demographics: formatDemographics(s.segmentation_data),
        date: s.conversation_date || '—',
        positive: p?.positive || '—',
        importance: (p?.positive_importance ?? null),
        importanceJustification: p?.positive_importance_justification || '—',
        quote: p?.positive_importance_quote || p?.quote || '—',
        href: `/conversations?search=${encodeURIComponent(s.customer_name || '')}`,
        rawStory: s,
        rawItem: p
      });
    }
  }

  return rows;
}

/* ---------- component ---------- */

export default function PositivesTable({ rowsSource = [], customerStories = [], jobName }) {
  const location = useLocation();

  const rows = React.useMemo(() => {
    // Preserve tiles order (already ranked like dashboard)
    return (rowsSource || []).map((t, i) => {
      const title = t.title || t.positive_category || t.category || '—';
      const positiveCategory = t.positive_category || t.category || title;
      const occurrences = t.occurrences ?? t.frequency ?? t.count ?? t.interviewCount ?? 0;

      const slug = slugify(positiveCategory || title);
      const linkTo = slug ? `/positive/${slug}${location.search || ''}` : null;

      const instances = buildInstancesFromCustomerStories({
        customerStories,
        jobName,
        positiveCategory
      });

      const avgImportance = instances.length
        ? (instances.reduce((sum, r) => sum + (Number(r.importance) || 0), 0) / instances.length)
        : null;

      return {
        key: `${slug || `row-${i}`}`,
        positiveCategory: positiveCategory || title,
        occurrences,
        linkTo,
        instances,
        avgImportance
      };
    });
  }, [rowsSource, location.search, customerStories, jobName]);

  const [open, setOpen] = React.useState({});
  const toggle = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));

  if (!rows.length) return null;

  return (
    <TableContainer component={Box} sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width={40} />
            <TableCell>Positive impression</TableCell>
            <TableCell align="right">Occurrences</TableCell>
            <TableCell align="right">Avg Importance</TableCell>
            <TableCell align="center" width={56}>Open</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <React.Fragment key={r.key}>
              <TableRow hover>
                <TableCell padding="checkbox">
                  <IconButton size="small" onClick={() => toggle(r.key)} aria-label="Expand row">
                    {open[r.key] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </IconButton>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {r.positiveCategory || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">{r.occurrences}</TableCell>
                <TableCell align="right">
                  {r.avgImportance != null ? (
                    <Chip size="small" label={F.positiveImportance(r.avgImportance).text} />
                  ) : '—'}
                </TableCell>
                <TableCell align="center">
                  {r.linkTo ? (
                    <Tooltip title="Open positive impression details">
                      <IconButton
                        size="small"
                        component={RouterLink}
                        to={r.linkTo}
                        aria-label="Open positive impression details"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="No detail page available">
                      <span>
                        <IconButton size="small" disabled>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>

              {/* Expanded sub-table */}
              <TableRow>
                {/* colSpan = 5 (matches parent header cols) */}
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                  <Collapse in={!!open[r.key]} timeout="auto" unmountOnExit>
                    <Box sx={{ p: 2, pl: 6 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Interviews
                      </Typography>

                      {r.instances && r.instances.length > 0 ? (
                        <Table size="small" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Demographics</TableCell>
                              <TableCell>Date</TableCell>
                              <TableCell>Positive (raw)</TableCell>
                              <TableCell align="right">Importance</TableCell>
                              <TableCell>Importance justification</TableCell>
                              <TableCell>Quote</TableCell>
                              <TableCell align="center" width={56}>Open</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {r.instances.map((inst) => (
                              <TableRow key={inst.key} hover>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {inst.name || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {inst.demographics || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {inst.date || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {inst.positive || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  {inst.importance != null ? F.positiveImportance(inst.importance).text : '—'}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {inst.importanceJustification || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {inst.quote || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title="Open interview">
                                    <span>
                                      <IconButton
                                        size="small"
                                        component="a"
                                        href={inst.href}
                                        target="_blank"
                                        rel="noopener"
                                        aria-label="Open interview"
                                      >
                                        <OpenInNewIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          No interviews found for this positive.
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
